import { toast } from "react-toastify";

import { PIN_TYPES } from "models";
import { DEFAULT_PIN_TYPES } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/hooks/useGetSelectedFilterValues";
import { ORDER_BY, REG_VIN_FILTER, setWarrantyDashboardFilters, useGetWarrantyDashboardFilters } from "modules/WarrantyDashboard/hooks";
import { WARRANTY_STORAGE_KEYS } from "modules/WarrantyDashboard/storageKeys";
import { copyToClipboard, setPreference } from "utils/common";

import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { Dispatch, SetStateAction } from "react";
import type { PAGE_SIZE } from "utils/common";

type WarrantyDashboardTableFilterHandlersProps = {
  setRegVinNumberFilter: Dispatch<SetStateAction<REG_VIN_FILTER>>;
};

const getClaimOrder = (claimed?: ORDER_BY | null) => {
  if (!claimed) return ORDER_BY.ClaimAscending;
  return claimed === ORDER_BY.ClaimAscending ? ORDER_BY.ClaimDescending : null;
};

export const useWarrantyDashboardTableFilterHandlers = ({ setRegVinNumberFilter }: WarrantyDashboardTableFilterHandlersProps) => {
  const { filters } = useGetWarrantyDashboardFilters();
  const { page: currentPage, page_size: pageSize, claim_order_by, pin_type_id } = filters;

  const handleCopyToClipboard = (content: string, message: string) => copyToClipboard(content).then(() => toast.success(message));

  const onPageChange = (data: { activePage: number }) => {
    setWarrantyDashboardFilters({ ...filters, page: data.activePage });
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
    onPageChange({ activePage: newState.pageIndex + 1 });
  };
  const handlePageSizeChange = (value: PAGE_SIZE) => {
    setPreference(WARRANTY_STORAGE_KEYS.PageSizePreference, value);
    setWarrantyDashboardFilters({ ...filters, page_size: value, page: 1 });
  };

  const handleRegVinNumberChange = (value: string[]) => {
    const isReg = value.some(el => Number(el) === REG_VIN_FILTER.Reg);
    setRegVinNumberFilter(isReg ? REG_VIN_FILTER.Reg : REG_VIN_FILTER.Vin);
  };

  const handleClaimChange = () => {
    const claimOrder = getClaimOrder(claim_order_by);
    setPreference(WARRANTY_STORAGE_KEYS.ClaimSortPreference, claimOrder);
    if (claimOrder) setPreference(WARRANTY_STORAGE_KEYS.OrderByPreference, ORDER_BY.AppointmentDateAscending);

    setWarrantyDashboardFilters({ ...filters, claim_order_by: claimOrder, ...(claimOrder && { order_by: ORDER_BY.AppointmentDateAscending }), page: 1 });
  };

  const handlePinTypeChange = (pinType: string[]) => {
    if (!pinType.length) return;

    const isAllOptionSelected = pinType.includes("all");
    const areAllPinTypesNotSelected = pin_type_id?.length !== DEFAULT_PIN_TYPES.length;
    const pinTypes = isAllOptionSelected && areAllPinTypesNotSelected ? DEFAULT_PIN_TYPES : pinType.map(Number).filter(Boolean);
    const isOnlyRemarksSelected = pinTypes.every(element => element === PIN_TYPES.Remarks);
    const isOnlyRecallsSelected = pinTypes.every(element => element === PIN_TYPES.Recall);

    setPreference(WARRANTY_STORAGE_KEYS.PinTypePreference, pinTypes);
    if (isOnlyRemarksSelected) {
      setPreference(WARRANTY_STORAGE_KEYS.WarrantyTypePreference, null);
      setPreference(WARRANTY_STORAGE_KEYS.PinStatusPreference, null);
      setPreference(WARRANTY_STORAGE_KEYS.ClaimedPreference, false);
    }

    if (isOnlyRecallsSelected) setPreference(WARRANTY_STORAGE_KEYS.WarrantyTypePreference, null);

    setWarrantyDashboardFilters({
      ...filters,
      pin_type_id: pinTypes,
      ...(isOnlyRemarksSelected && { warranty_type_id: null, claimed: false, pin_status_id: null }),
      ...(isOnlyRecallsSelected && { warranty_type_id: null }),
      page: 1
    });
  };
  const handleAppointmentChange = (order: string[]) => {
    const updatedSortBy = Number(order[0]);

    setPreference(WARRANTY_STORAGE_KEYS.OrderByPreference, updatedSortBy);
    setPreference(WARRANTY_STORAGE_KEYS.ClaimSortPreference, null);
    setWarrantyDashboardFilters({ ...filters, order_by: updatedSortBy, claim_order_by: null, page: 1 });
  };

  const handleWarrantyTypeChange = (warrantyType: string[]) => {
    const isAllOptionSelected = warrantyType.includes("all");
    const updatedWarrantyType = isAllOptionSelected ? null : Number(warrantyType[0]);

    setPreference(WARRANTY_STORAGE_KEYS.WarrantyTypePreference, updatedWarrantyType);
    setWarrantyDashboardFilters({ ...filters, warranty_type_id: updatedWarrantyType, page: 1 });
  };

  const handleQuestionChange = (status: string[]) => {
    const isAllOptionSelected = status.includes("all");
    const selectedStatus = isAllOptionSelected ? null : Number(status[0]);
    setPreference(WARRANTY_STORAGE_KEYS.QuestionStatusPreference, selectedStatus);

    setWarrantyDashboardFilters({ ...filters, status: selectedStatus, page: 1 });
  };

  const handleLocationSortChange = (order: string[]) => {
    const updatedSortBy = Number(order[0]);

    setPreference(WARRANTY_STORAGE_KEYS.LocationOrderByPreference, updatedSortBy);
    setWarrantyDashboardFilters({ ...filters, order_by_location: updatedSortBy, page: 1 });
  };

  return {
    handleClaimChange,
    handlePinTypeChange,
    handleQuestionChange,
    handlePageSizeChange,
    handleCopyToClipboard,
    handlePaginationChange,
    handleAppointmentChange,
    handleWarrantyTypeChange,
    handleRegVinNumberChange,
    handleLocationSortChange
  };
};
